import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import Flag from 'react-flagpack';

import { useAppDispatch } from '../../../app/hooks';
import { StopoverScheduleIcon } from '../../../assets/icons/stopoverSchedule';
import {
  Button,
  DefaultLayout,
  ErrorBoundary,
  ExemptTag,
  List,
} from '../../../components/index';
import { useBlockNavigation, useChangePagination } from '../../../hooks';
import {
  useGetChargeByIdQuery,
  useGetChargesQuery,
} from '../../../services/chargeApi';
import { tosApi } from '../../../services/tosApi';
import {
  FilterDefaultLayoutType,
  Stopover as StopoverType,
} from '../../../types';
import { createDateStringPtBr } from '../../../utils/utils';
import { CardRenderer } from '../operatorArea/styles';
import { ChargeDrawer } from './chargeDrawer/ChargeDrawer';
import { ChargeStatusTag } from './ChargeStatusTag';
import { getFareExemptionTooltip } from './exemption';
import { InvoicingDetails } from './invoicingDetails';

// PROCESSING e SEND_ERROR vencem sobre os demais na derivação do status da
// cobrança, então uma cobrança em transmissão só aparece nas abas que os
// listem. Sem isso ela some da tela justamente enquanto o faturista espera o
// desfecho. Cada um ganha aba própria, e não uma aba de transmissão comum, nem
// um lugar junto das pendências de faturamento: os dois pedem ações opostas do
// faturista, esperar e reenviar, e mantê-los em "A faturar/estornado" faria o
// rótulo descrever dois dos cinco valores que a aba filtra. Continuam em
// "Todas". Os rótulos são os mesmos das badges de tarifa, que não citam o RM.
// A aba "NF emitida" saiu junto: ela filtrava só por DONE, que não existe em
// FareStatus nem em ChargeStatus, e o filtro é por status de tarifa, então ela
// era permanentemente vazia. DONE também sai de "Todas", onde era inerte, e
// INVOICING sai pelo mesmo motivo: ele nunca existiu em FareStatus, que é o que
// o filtro consulta, e acabou removido de ChargeStatus também.
const tabs = [
  {
    label: 'Todas',
    value: 'BILLING,REVERSED,BILLED,REBILLED,PROCESSING,SEND_ERROR',
  },
  {
    label: 'A faturar/estornado',
    value: 'BILLING,REVERSED',
  },
  { label: 'Processando', value: 'PROCESSING' },
  { label: 'Erro no envio', value: 'SEND_ERROR' },
  { label: 'Faturado', value: 'BILLED,REBILLED' },
  { label: 'Arquivado', value: 'ARCHIVED' },
];

// A pergunta "existe tarifa em transmissão?" não pode sair da lista carregada
// agora que os dois estados têm aba própria: a cobrança em trânsito deixa de
// casar com "A faturar/estornado" e sai da lista, o que desligaria a
// revalidação exatamente onde o faturista espera o desfecho, e a linha não
// voltaria sozinha. Esta sonda pergunta ao servidor com filtro próprio, então
// independe da aba aberta. Ela é a única coisa polada na tela, e é barata:
// devolve só as cobranças em trânsito, que no caso comum são zero, o que dá 4
// consultas e 0,1 KB por busca (medido no banco de dev).
const PROCESSING_PROBE_FILTER = { page: 1, status: 'PROCESSING' };

// Página do ChargeController, e o teto que ele aceita em page_size
// (MAX_PAGE_SIZE). O teto tem que ser o mesmo dos dois lados: o pedido é a janela
// inteira, então se o cliente pedir acima do que o servidor entrega a lista para
// de crescer e a rolagem trava.
const PAGE_SIZE = 10;
const MAX_LIST_WINDOW = 500;

function getCardRenderer(stopover: StopoverType) {
  const [showList, setShowList] = useState(false);
  if (stopover.charge.charge_type === 'DOCKING') {
    return (
      <>
        <CardRenderer>
          <div style={{ paddingTop: '5px' }}>
            <ErrorBoundary replace="">
              <Flag
                code={stopover.charge.stopover?.vessel_flag}
                hasBorder={false}
                size="m"
              />
            </ErrorBoundary>
          </div>
          <div className="item-data">
            <div className="space-between">
              <div>
                <span className="title">
                  {stopover.charge?.stopover?.vessel_name}
                </span>
              </div>
              <span>
                ATR:{' '}
                <span className="color-neutral-medium code">
                  {stopover.charge.docking?.code || 'Código não gerado'}
                </span>
              </span>
            </div>
            <div className="space-between">
              <span className="subtitle">
                {stopover.charge.stopover?.shipping_agency_name}
              </span>
              <span className="color-neutral-medium code">
                {stopover.charge.stopover?.code}
              </span>
            </div>
            <div className="space-between" style={{ alignItems: 'baseline' }}>
              <div>
                <span>Primeiro cabo amarrado: </span>
                <span className="color-neutral-medium">
                  {createDateStringPtBr(
                    stopover.charge.docking?.first_cable_tied_in
                  )}
                </span>
              </div>
              <Button
                type="link"
                icon={showList ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setShowList((prev) => !prev)}
              />
            </div>
            <div>
              <span>Último cabo desamarrado: </span>
              <span className="color-neutral-medium">
                {createDateStringPtBr(
                  stopover.charge.docking?.last_cable_untied_in
                )}
              </span>
            </div>
            <div className="space-between">
              <div>
                <span>Berço:</span>
                <span className="color-neutral-medium">
                  {stopover.charge.docking?.docking_place_name}
                </span>
              </div>
              <div>
                <span>IMO:</span>
                <span className="color-neutral-medium code">
                  {stopover.charge.stopover?.vessel_imo}
                </span>
              </div>
            </div>
            <ChargeStatusTag
              style={{
                minWidth: '80px',
                width: 'fit-content',
                alignSelf: 'flex-start',
                textAlign: 'center',
                marginBottom: '10px',
              }}
              status={stopover.charge.status || 'BILLING'}
            />
          </div>
        </CardRenderer>
        {showList && (
          <List
            style={{ borderTop: '1px solid var(--neutral_lighter)' }}
            dataSource={stopover.charge.fare}
            renderItem={(item: any) => (
              <List.Item>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ color: 'var(--neutral_medium)' }}>
                    {item.company.name}
                  </span>
                  <ChargeStatusTag status={item.status || 'BILLING'} />
                  {item.is_exempt && (
                    <ExemptTag tooltip={getFareExemptionTooltip(item.status)} />
                  )}
                </div>
              </List.Item>
            )}
          />
        )}
      </>
    );
  }
  if (stopover.charge.charge_type === 'VESSEL') {
    return (
      <>
        <CardRenderer>
          <div style={{ paddingTop: '5px' }}>
            <ErrorBoundary replace="">
              <Flag
                code={stopover.charge?.vessel?.flag}
                hasBorder={false}
                size="m"
              />
            </ErrorBoundary>
          </div>
          <div className="item-data">
            <div className="space-between">
              <div>
                <span className="title">{stopover.charge?.vessel?.name}</span>
              </div>
              <span className="color-neutral-medium">
                EMBARCAÇÃO ESTACIONÁRIA
              </span>
            </div>
            <div className="space-between">
              <span className="subtitle">
                {stopover.charge?.vessel?.shipowner?.name}
              </span>
              <Button
                type="link"
                icon={showList ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setShowList((prev) => !prev)}
              />
            </div>
            <div>
              <span>Inclusão do item: </span>
              <span className="color-neutral-medium">
                {createDateStringPtBr(stopover.charge.created_at)}
              </span>
            </div>
            <ChargeStatusTag
              style={{
                minWidth: '80px',
                width: 'fit-content',
                alignSelf: 'flex-start',
                textAlign: 'center',
                marginBottom: '10px',
              }}
              status={stopover.charge.status || 'BILLING'}
            />
          </div>
        </CardRenderer>
        {showList && (
          <List
            style={{ borderTop: '1px solid var(--neutral_lighter)' }}
            dataSource={stopover.charge.fare}
            renderItem={(item: any) => (
              <List.Item>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span style={{ fontWeight: '600' }}>{item.company.name}</span>
                  <span style={{ color: 'var(--neutral_medium)' }}>
                    {item.company.identifier}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <ChargeStatusTag status={item.status || 'BILLING'} />
                  {item.is_exempt && (
                    <ExemptTag tooltip={getFareExemptionTooltip(item.status)} />
                  )}
                </div>
              </List.Item>
            )}
          />
        )}
      </>
    );
  }

  return (
    <CardRenderer>
      <StopoverScheduleIcon />
      <div className="item-data">
        <div className="space-between">
          <div>
            <span className="title">{stopover.charge?.company?.name}</span>
          </div>
          <span className="color-neutral-medium">RESPONSÁVEL</span>
        </div>
        <div>
          <span>Inclusão do item: </span>
          <span className="color-neutral-medium">
            {createDateStringPtBr(stopover.charge.created_at)}
          </span>
        </div>
        <ChargeStatusTag
          style={{
            minWidth: '80px',
            width: 'fit-content',
            alignSelf: 'flex-start',
            textAlign: 'center',
          }}
          status={stopover.charge.status || 'BILLING'}
        />
      </div>
    </CardRenderer>
  );
}

export function Invoicing() {
  const dispatch = useAppDispatch();
  const { onChangePagination, queryPage } = useChangePagination();
  const { blockNavigate, setBlockNavigate, hasEdited, setHasEdited } =
    useBlockNavigation();

  // `page` é sempre 1 e `page_size` é a janela: o DefaultLayout cresce o
  // page_size conforme a rolagem, em vez de pedir a página seguinte, para que
  // toda resposta seja a lista inteira e não uma fatia.
  const [filter, setFilter] = useState<FilterDefaultLayoutType>({
    page: 1,
    page_size: PAGE_SIZE,
    status: '',
  });
  const [isVisibleAddRateDrawer, setisVisibleAddRateDrawer] = useState(false);
  const [selectedStopover, setSelectedStopover] = useState<StopoverType>(
    {} as StopoverType
  );

  // `currentData` é undefined enquanto a resposta do argumento atual não chega, e
  // `data` retém a do argumento anterior. É a diferença entre os dois que diz ao
  // DefaultLayout se a lista na tela responde ao filtro atual: sem ela, a busca e
  // a troca de aba exibem o resultado do filtro antigo como se fosse o novo.
  const {
    data: chargesData,
    currentData: currentChargesData,
    isLoading: isLoadingChargesData,
    isFetching: isFetchingChargesData,
  } = useGetChargesQuery(filter);

  // Derivado do servidor, e não de a geração ter sido acionada nesta sessão:
  // cobre a tarifa que já estava em trânsito quando a tela abriu, por envio de
  // outro usuário ou aba reaberta, e zera sozinho quando a última sai de
  // PROCESSING. A sonda é buscada no mount e revalidada pela invalidação da tag
  // Charge que o generate_statement dispara, então o clique do faturista liga o
  // polling sem precisar de estado de sessão.
  const { data: processingProbe } = useGetChargesQuery(PROCESSING_PROBE_FILTER);

  const hasProcessingFare = useMemo(() => {
    return (processingProbe?.results?.length || 0) > 0;
  }, [processingProbe]);

  // Segunda assinatura da sonda, só para a sonda dirigir o próprio polling. O
  // pollingInterval precisa do resultado da própria query, que só existe depois
  // da chamada acima; assinar de novo com o mesmo argumento reaproveita a mesma
  // entrada de cache, então não há requisição a mais além da revalidação, e a
  // troca liga e desliga o polling no mesmo render em que os dados mudam.
  useGetChargesQuery(PROCESSING_PROBE_FILTER, {
    pollingInterval: hasProcessingFare ? 5000 : 0,
  });

  // O painel de detalhe não vem de getCharges: o DefaultLayout busca a cobrança
  // selecionada por getChargeById e copia o resultado para selectedStopover, que é
  // de onde saem a badge da tarifa, o protocolo, o motivo da recusa e as
  // condicionais dos botões. Esta assinatura existe para o detector abaixo saber
  // se o painel está em voo, e para a entrada do painel ter assinante na hora da
  // invalidação, que é o que a rebusca em vez de descartá-la do cache; é o mesmo
  // argumento que o DefaultLayout usa, então é a mesma entrada e não custa
  // requisição.
  //
  // Sem pollingInterval de propósito. Durante o PROCESSING o painel não tem nada
  // que mude: o FareTransmissionDetail e o FareTransmissionAlert devolvem null
  // fora de BILLED/REBILLED e de SEND_ERROR, e todo travamento de botão é função
  // de `fare.status`, que só muda no desfecho. Ou seja, cada busca redesenhava
  // tela idêntica, a 94 a 422
  // consultas por requisição (o detalhe converte as sete tabelas por tarifa), por
  // até 30 min. Os dois caminhos que trazem dado novo continuam cobertos: o clique
  // no card, porque o trigger do lazy query do DefaultLayout força busca
  // (`preferCacheValue` é false por padrão), e a transição, pelo detector.
  const selectedChargeId = selectedStopover?.charge?.id;

  const { isFetching: isFetchingSelectedCharge } = useGetChargeByIdQuery(
    selectedChargeId as number,
    { skip: !selectedChargeId }
  );

  // Nada além da sonda é polado: é ela que detecta a mudança. Polar a lista
  // custava a janela inteira a cada 5s, e polar o painel custava até 422 consultas
  // a cada 5s, por até 30 min, para descobrir uma transição que a sonda enxerga
  // com 4 consultas e payload vazio. O que ela dispara é a invalidação da tag
  // `Charge`, quando o estado da transmissão muda: começou, acabou, ou uma das
  // várias terminou.
  //
  // A invalidação, e não a rebusca das assinaturas daqui, porque a lista de cada
  // aba é uma entrada de cache própria e só a da aba aberta tem assinante. As
  // outras ficam no cache com a lista de antes, e trocar de aba dentro dessa
  // janela servia esse cache sem pedir nada ao servidor: a cobrança que acabava de
  // falhar não aparecia na aba "Erro no envio". A invalidação alcança as duas
  // situações, porque rebusca a entrada assinada e descarta do cache a que ninguém
  // assina, que é o que faz a aba seguinte buscar de novo.
  //
  // A chave é por tarifa, e não por cobrança, porque a unidade de transmissão é a
  // tarifa: `generate_statement` é acionado por tarifa e o travamento dos botões é
  // por tarifa. Chaveando por cobrança, duas tarifas da mesma cobrança em trânsito
  // faziam o desfecho da primeira não chegar à tela, porque a cobrança continuava
  // derivando PROCESSING por causa da segunda e a chave não mudava. A sonda
  // devolve todas as tarifas de cada cobrança em trânsito, com id e status, então
  // o par id:status já cobre isso sem pedir nada novo ao servidor.
  //
  // O `count` entra na chave porque a sonda devolve no máximo uma página: com mais
  // transmissões simultâneas do que isso a lista vem truncada, e sem o `count` uma
  // mudança de tamanho do conjunto passaria batido. Fica de fora só o caso de uma
  // entrar e outra sair no mesmo intervalo, as duas fora da primeira página, que a
  // transição seguinte corrige.
  const transmissionKey = useMemo(() => {
    if (!processingProbe?.results) return undefined;

    const fares = processingProbe.results
      .flatMap((item) => item.charge?.fare || [])
      .map((fare) => `${fare.id}:${fare.status}`)
      .sort()
      .join(',');

    return `${processingProbe.count}:${fares}`;
  }, [processingProbe]);

  // A primeira resposta da sonda não é transição, é o estado inicial, e rebuscar
  // nela seria uma requisição a mais em todo carregamento da tela.
  const seenTransmissionKey = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (transmissionKey === undefined) return;

    if (seenTransmissionKey.current === undefined) {
      seenTransmissionKey.current = transmissionKey;
      return;
    }

    if (seenTransmissionKey.current === transmissionKey) return;

    // Rebuscar sobre requisição em voo é no-op: o `condition` do queryThunk recusa
    // quando já existe uma pendente para a mesma chave de cache. E a que está em
    // voo pode ter sido montada antes desta transição, então marcá-la como
    // atendida aqui perderia a rebusca sem nova tentativa. Espera: o efeito roda
    // de novo quando o `isFetching` cair, e aí a chave ainda será a nova.
    if (isFetchingChargesData || isFetchingSelectedCharge) return;

    seenTransmissionKey.current = transmissionKey;

    // O painel entra junto com a lista, e não vem de getCharges: sem ele a tela
    // ficaria parada em "Processando" com o card à esquerda já no desfecho, até o
    // faturista clicar em outro card e voltar.
    dispatch(tosApi.util.invalidateTags([{ type: 'Charge' }]));
  }, [transmissionKey, isFetchingChargesData, isFetchingSelectedCharge]);

  return (
    <>
      <ChargeDrawer
        visibleDrawer={isVisibleAddRateDrawer}
        closeDrawer={() => {
          setisVisibleAddRateDrawer(false);
        }}
        hasEdited={hasEdited}
        setHasEdited={setHasEdited}
        setBlockNavigate={setBlockNavigate}
        blockNavigate={blockNavigate}
        setSelectedStopover={setSelectedStopover}
      />
      <DefaultLayout
        title="Faturamento"
        tabs={tabs}
        filterField="status"
        setFilter={setFilter}
        stopoveresData={chargesData}
        isLoadingStopoveresData={isLoadingChargesData}
        isFetchingStopoveresData={isFetchingChargesData}
        hasListDataForCurrentFilter={currentChargesData !== undefined}
        pageSize={PAGE_SIZE}
        maxListWindow={MAX_LIST_WINDOW}
        queryPage={queryPage}
        onChangePagination={onChangePagination}
        selectedStopover={selectedStopover}
        setSelectedStopover={setSelectedStopover}
        visibleAddDataDrawer={isVisibleAddRateDrawer}
        setVisibleAddDataDrawer={setisVisibleAddRateDrawer}
        labelAddDataDrawer="Tarifa avulsa"
        labelPlural="ITENS"
        pageContext="INVOICING"
        cardRenderer={getCardRenderer}
      >
        <InvoicingDetails
          name="invoicing_form"
          selectedStopover={selectedStopover}
          setSelectedStopover={setSelectedStopover}
          blockNavigate={blockNavigate}
          setBlockNavigate={setBlockNavigate}
          hasEdited={hasEdited}
          setHasEdited={setHasEdited}
        />
      </DefaultLayout>
    </>
  );
}
